// PRFAQSection.js - Portugal
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const PRFAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'How much do I have to invest for a Golden Visa?',
      answer: 'The floor is €250,000, and that applies only to cultural heritage donations. Capital transfer, investment funds and scientific research each sit at €500,000. The job creation option asks for 10 full-time positions rather than a sum of money. One thing worth knowing: real estate in coastal areas has been phased out, and the emphasis has moved to interior regions and to funds.'
    },
    {
      question: 'How long do I actually have to be in the country?',
      answer: 'Very little, on the Golden Visa: 7 days a year on average, or 14 days across a 2-year period. The D7 is heavier, requiring 16 months within the first 2 years. When you reach the permanent residency or citizenship stage, you also have to show genuine ties to Portugal on top of meeting the minimum stay.'
    },
    {
      question: 'Does a Golden Visa let me work?',
      answer: 'It does. The permit carries the right to work in Portugal and across the EU, even though the programme was built around investors who have no need of a Portuguese job. D7 holders can work as well, although that visa assumes you are living principally on passive income.'
    },
    {
      question: 'What income does the D7 call for?',
      answer: 'It is pegged to the Portuguese minimum wage. The main applicant needs €820 a month, a spouse adds 50% on top of that at €410, and each dependent child adds 30%, or €246. The income has to be recurring and stable, drawn from a pension, rent, investments or remote work.'
    },
    {
      question: 'When can I naturalise?',
      answer: 'Five years of legal residency is the point at which Portuguese citizenship becomes available. You will need Portuguese at A2 level, a clean criminal record, demonstrable ties to the country and a record of meeting the minimum stay. Dual citizenship is permitted, so you need not give up the passport you hold.'
    },
    {
      question: 'Can my family come with me?',
      answer: 'Yes. Family reunification runs on both the Golden Visa and the D7. A spouse or partner can be added, as can dependent parents and any dependent children who are under 18, or under 26 if they are still studying. Each person added attracts a further government fee.'
    },
    {
      question: 'How does the Non-Habitual Resident (NHR) regime work?',
      answer: 'NHR is a separate tax status that runs for 10 years. Income arising abroad, such as pensions, dividends or rent, may be exempt altogether or taxed at a reduced rate, while Portuguese income from high-value activities is taxed at a flat 20%. The catch is the entry condition: you cannot have been a Portuguese tax resident at any point in the previous 5 years.'
    },
    {
      question: 'Will I need the language?',
      answer: 'Not to get residency. Neither the Golden Visa nor the D7 tests you on it. Portuguese at A2 level only enters the picture at the citizenship stage, 5 years later. In the meantime English is widely spoken, particularly in Lisbon and Porto, and language courses are easy to come by.'
    },
    {
      question: 'Am I locked into the investment?',
      answer: 'For 5 years, yes. That is the minimum holding period on the Golden Visa. Once permanent residency or citizenship has come through, you are free to liquidate. Plenty of investors keep the position anyway, on the view that the returns and the lifestyle both justify it.'
    },
    {
      question: 'How likely is an application to be approved?',
      answer: 'Complete applications that meet every requirement do well. Golden Visa cases with investment documentation in order are approved at roughly 90-95%, and D7 cases backed by adequate proof of income land in much the same range. We do not publish an approval rate of our own.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            Questions We Get Asked
          </h2>
          <p className="text-gray-600 text-base font-light">
            Straight answers on the Golden Visa, the D7 and what follows either one
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="bg-surface rounded-lg shadow-sm overflow-hidden border border-gray-200"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
              >
                <span className="text-base font-semibold text-primary pr-4">
                  {faq.question}
                </span>
                <div className="flex-shrink-0">
                  {openIndex === index ? (
                    <Minus size={24} className="text-secondary" />
                  ) : (
                    <Plus size={24} className="text-secondary" />
                  )}
                </div>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 text-gray-600 text-sm font-light leading-relaxed border-t border-gray-200 pt-4">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6 text-center"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed">
            <strong>Not covered here?</strong> Speak to an adviser at no cost. They will go through your circumstances and set out which Portuguese route is realistic for you.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PRFAQSection;
